import { useState, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
// I messaggi del server arrivano in inglese: qui diventano la frase giusta
// nella lingua di chi legge. Vedi utils/erroreAutenticazione.js.
import { erroreAutenticazione } from '../utils/erroreAutenticazione';
import { useAuth } from '../hooks/useAuth';
import { useEmailTypoSuggestion } from '../hooks/useEmailTypoSuggestion';
// apiRecoveryReset non si chiama piu' da qui: la scrittura distruttiva la fa
// useAuth.ripristinaDaChiaveDiRecupero, dopo le tre verifiche.
import { apiRecoveryVerify, apiInterruttori } from '../utils/api';
// Nessun import da utils/crypto in questa pagina, ed e' voluto (tappa 3).
// Prima ce n'erano sette, di cui uno — deriveKey — importato e mai chiamato,
// e uno — decryptKeyFromRecovery — usato dentro un try/catch che in caso di
// errore proseguiva lo stesso. Tutta la crittografia del ripristino sta ora
// in useAuth.ripristinaDaChiaveDiRecupero, in un posto solo, dove l'ordine
// dei controlli si legge tutto insieme.

// Standard eye / eye-with-slash outline icons for the password show/hide
// toggle — plain SVG (no icon library dependency) so they render crisp and
// consistent at small size, unlike an emoji glyph (which varies by OS/font
// and reads ambiguously as a toggle, especially on mobile).
function EyeIcon(props) {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" {...props}>
      <path d="M1 12s4-7 11-7 11 7 11 7-4 7-11 7-11-7-11-7Z"/>
      <circle cx="12" cy="12" r="3"/>
    </svg>
  );
}
function EyeOffIcon(props) {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" {...props}>
      <path d="M17.94 17.94A10.94 10.94 0 0 1 12 20c-7 0-11-8-11-8a19.66 19.66 0 0 1 5.06-5.94M9.9 4.24A10.4 10.4 0 0 1 12 4c7 0 11 8 11 8a19.65 19.65 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24"/>
      <line x1="1" y1="1" x2="23" y2="23"/>
    </svg>
  );
}

// Il recupero password e' aperto? LO DICE IL SERVER (2026-09-16), via
// GET /auth/interruttori: prima era una variabile di build
// (REACT_APP_CAMBIO_PASSWORD_ATTIVO) che nessun file conteneva, e ogni build
// dal 12/09 spegneva il recupero senza dirlo. Finche' la risposta non
// arriva si assume aperto: un modulo che poi riceve 503 vale piu' di un
// avviso «non disponibile» mostrato per sbaglio a chi ha perso la password.

export default function AuthPage({ onHome, initialEmail = '', initialBuyIntent = null, initialEntrata = null, onSwitchToLogin, onSwitchToRegister, onTerms, onPrivacy, onRegistered, forzaLogin = false }) {
  const { t } = useTranslation('auth');
  const [recuperoAttivo, setRecuperoAttivo] = useState(true);
  useEffect(() => {
    let vivo = true;
    apiInterruttori().then((r) => { if (vivo && r && r.cambioPasswordAttivo === false) setRecuperoAttivo(false); }).catch(() => {});
    return () => { vivo = false; };
  }, []);
  const { login, register, pending2fa, pending2faError, submit2fa, cancel2fa,
          ripristinaDaChiaveDiRecupero } = useAuth();

  // Derived straight from the URL on every render, not tracked in state — a
  // direct link, a refresh, or the switch link below (a real pushState, see
  // onSwitchToLogin/onSwitchToRegister) all just work without this page
  // needing to be told separately what mode it's in. Recovery has no URL of
  // its own (entered from within /login), so it stays a plain boolean.
  // forzaLogin: chi ha gia' una sessione su questo dispositivo e apre
  // /register non deve vedere il modulo di registrazione (ne' — come accadeva
  // fino al 2026-09-02 — la schermata di sblocco). Vede il modulo di ACCESSO
  // con una riga che spiega perche'.
  const mode = forzaLogin ? 'login' : (window.location.pathname === '/register' ? 'register' : 'login');
  const [showRecovery, setShowRecovery] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error,   setError]   = useState('');

  // Login fields — "identifier" because it now accepts either the email
  // (what every user actually has, since register no longer asks for a
  // username) or a legacy/custom username (see backend findUserByIdentifier).
  // initialEmail vale anche qui (2026-09-06): chi arriva dalla schermata di
  // pagamento riuscito dopo aver pagato attraverso il ponte si trova in un
  // browser dove non ha mai fatto l'accesso, e l'indirizzo lo ha creato un
  // minuto prima. Prima questo campo partiva sempre vuoto: l'email arrivava
  // fino a qui e veniva buttata via proprio sull'ultimo passo.
  const [loginIdentifier, setLoginIdentifier] = useState(initialEmail || '');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);

  // Register fields — just email + password. Username is auto-generated
  // server-side from the email and shown/editable later in Profile.
  // initialEmail comes from the homepage hero signup box (see App.js's
  // goToRegister/prefillEmail) — in-memory navigation state only, so a
  // direct link, a refresh, or a shared /register URL always starts empty.
  const [email, setEmail] = useState(initialEmail || '');
  const emailTypo = useEmailTypoSuggestion(email);
  // Arriving with a prefilled email (from the hero) starts in "confirm" mode
  // — email shown as read-only text with a Change link, password the only
  // visible/focused field — instead of the two-field form. No initialEmail
  // (direct link, refresh, share) means showEmailField starts true and this
  // never differs from today's behavior.
  const [showEmailField, setShowEmailField] = useState(!initialEmail);
  // Whether this page load came from the hero — drives hiding the Login/
  // Register tabs entirely (replaced by a fixed "Almost there" title) for
  // the whole visit, independent of showEmailField toggling via "Change":
  // someone who arrived from the hero already chose to register, so there's
  // no login tab to offer even while they're mid-edit on their email.
  const arrivedFromHero = !!initialEmail;

  // Recovery token display after register
  const [recoveryToken,      setRecoveryToken]      = useState('');
  const [registeredUsername, setRegisteredUsername] = useState(''); // server-assigned, for the recovery-file label/filename
  const [tokenCopied,   setTokenCopied]   = useState(false);
  const [registered,    setRegistered]    = useState(false);

  // Recovery flow
  const [recoveryStep,     setRecoveryStep]     = useState(1); // 1=enter username+token, 2=new password
  const [recoveryUsername, setRecoveryUsername] = useState('');
  const [recoveryTokenIn,  setRecoveryTokenIn]  = useState('');
  const [recoveryData,     setRecoveryData]     = useState(null); // { encryptedKeyBackup, salt }
  const [newPassword,      setNewPassword]      = useState('');
  const [newConfirm,       setNewConfirm]        = useState('');

  // 2FA boxes
  const [code, setCode] = useState(['','','','','','']);
  const codeRefs = [useRef(),useRef(),useRef(),useRef(),useRef(),useRef()];

  // ── Login ─────────────────────────────────────────────
  async function handleLogin(e) {
    e.preventDefault(); setError('');
    setLoading(true);
    try { await login(loginIdentifier, password); }
    catch(err) { setError(erroreAutenticazione(err, t)); }
    finally { setLoading(false); }
  }

  // ── Register ──────────────────────────────────────────
  async function handleRegister(e) {
    e.preventDefault(); setError('');
    if (password.length < 8) return setError(t('err_password_too_short'));
    setLoading(true);
    try {
      const data = await register(email, password, {
        emailTypoSuggestionShown: emailTypo.everShown,
        emailTypoAccepted: emailTypo.accepted,
        // Quale elemento ha aperto questa registrazione (null = /register
        // diretto). Deciso dal chiamante in App.js, salvato dal server.
        registerEntry: initialEntrata || (initialBuyIntent?.campaign ?? null),
      });
      setRegisteredUsername(data.user.username);
      setRecoveryToken(data.recoveryToken);
      setRegistered(true);
      // 2026-08-28: register() above sets `user`+`encKey` in the same tick
      // (useAuth.js), which flips isAuthenticated to true — AppRouter
      // (App.js) re-renders on THAT, and its `if (!isAuthenticated)` guard
      // means it stops rendering AuthPage at all the instant that happens,
      // unmounting this component before the setRegistered(true) screen
      // above ever gets to paint. Confirmed: that screen was already dead
      // code, unreachable in practice — this app actually lands people
      // straight in the Vault, whose own sessionStorage-driven recovery
      // popup (zv_pending_recovery, set inside register() itself) is what
      // was silently carrying the "show the recovery key" job alone.
      // onRegistered (App.js) sets page='post-register' in this SAME
      // batch, so the very next authenticated render shows that page
      // instead of falling through to Vault — same race, used on purpose
      // this time. Left the code above in place rather than deleting it:
      // if onRegistered is ever missing/no-ops, this remains a working
      // fallback instead of a silent dead end.
      onRegistered?.(initialBuyIntent);
    } catch(err) { setError(erroreAutenticazione(err, t)); }
    finally { setLoading(false); }
  }

  // ── Copy recovery token ────────────────────────────────
  function copyToken() {
    navigator.clipboard.writeText(recoveryToken);
    setTokenCopied(true);
    setTimeout(() => setTokenCopied(false), 2000);
  }

  // ── Recovery step 1: verify token ─────────────────────
  async function handleRecoveryVerify(e) {
    e.preventDefault(); setError('');
    setLoading(true);
    try {
      const data = await apiRecoveryVerify(recoveryUsername, recoveryTokenIn);
      setRecoveryData(data);
      setRecoveryStep(2);
    } catch(err) { setError(spiegaErroreRecupero(err)); }
    finally { setLoading(false); }
  }

  // CHI NON PASSA IL CANCELLO DEVE CAPIRE COSA FARE (2026-09-12).
  //
  // Il server risponde 403 con un codice: 'senza_chiave_recupero' per chi la
  // chiave non l'ha mai generata, 'app_vecchia' per chi ha ancora l'APK
  // congelata. Sono due situazioni diverse e hanno due rimedi diversi —
  // "generala dal profilo" e "aggiorna l'app" — quindi due frasi, nella
  // lingua dell'utente. Prima arrivava un "not_enabled" buono per nessuno.
  function spiegaErroreRecupero(err) {
    const codice = err && err.codice;
    if (codice === 'senza_chiave_recupero') return t('recovery_err_senza_chiave');
    if (codice === 'app_vecchia')           return t('recovery_err_app_vecchia');
    // La copia di recupero non si apre con questa chiave: testo vero, mai il
    // nome della classe (prima a schermo compariva «busta non apribile»).
    if (err && err.name === 'BustaNonApribile') return t('recovery_err_chiave_non_corrisponde');
    return (err && err.message) || t('err_reset_generic');
  }

  // ── Recovery step 2: reset password ───────────────────
  // ── PUNTO 8 di 8 (tappa 3, 2026-09-10) ──────────────────────────────────
  //
  // Cosa faceva questa funzione prima, e perche' era spenta:
  //
  //   1. importava deriveKey e non lo chiamava mai (riga 217, morta);
  //   2. se il recupero della chiave falliva, PROSEGUIVA LO STESSO — con un
  //      console.warn che diceva "user will need to re-upload files". Cioe':
  //      sovrascriveva la password sapendo di aver perso la chiave, e
  //      chiamava "ricaricare i file" quella che era la perdita definitiva
  //      di tutto il contenuto della cassaforte. Nessuno lo leggeva, quel
  //      warn: e' una console del browser di una persona che sta cercando
  //      di rientrare nel proprio account.
  //
  // Adesso non si scrive niente finche' non e' verificato tutto, e se una
  // sola verifica non passa la password resta quella di prima — che e'
  // l'unica cosa che permette di riprovare. Vedi
  // useAuth.ripristinaDaChiaveDiRecupero per l'ordine dei quattro passi.
  async function handleRecoveryReset(e) {
    e.preventDefault(); setError('');
    if (newPassword !== newConfirm) return setError(t('err_passwords_mismatch'));
    if (newPassword.length < 8)     return setError(t('err_password_too_short'));
    setLoading(true);
    try {
      const esito = await ripristinaDaChiaveDiRecupero(recoveryUsername, recoveryTokenIn, newPassword);
      if (!esito.token) throw new Error(t('err_reset_failed'));

      // La chiave di recupero NUOVA va mostrata: quella vecchia e' stata
      // consumata, e un utente che esce di qui credendo di averne ancora una
      // valida e' un utente senza rete la prossima volta.
      sessionStorage.setItem('zv_pending_recovery', esito.nuovoToken);
      sessionStorage.setItem('zv_pending_recovery_user', recoveryUsername);

      setShowRecovery(false);
      setError('');
      setRecoveryStep(1);

      if (esito.provaSuFileVero === 'nessun-file') {
        // Non si spaccia un controllo saltato per un controllo superato.
        alert(t('recovery_reset_success_alert') + '\n\n' + t('avviso_recupero_senza_file'));
      } else {
        alert(t('recovery_reset_success_alert'));
      }
    } catch(err) {
      setError(spiegaErroreRecupero(err));
    }
    finally { setLoading(false); }
  }

  // ── 2FA ───────────────────────────────────────────────
  async function handle2fa(e) {
    e.preventDefault(); setError('');
    const fullCode = code.join('');
    if (fullCode.length !== 6) return setError(t('err_enter_all_digits'));
    setLoading(true);
    try { await submit2fa(fullCode); }
    catch { }
    finally { setLoading(false); }
  }
  function handleCodeInput(i, val) {
    const digit = val.replace(/\D/g,'').slice(-1);
    const next = [...code]; next[i] = digit; setCode(next);
    if (digit && i < 5) codeRefs[i+1].current?.focus();
  }
  function handleCodeKey(i, e) {
    if (e.key==='Backspace' && !code[i] && i>0) codeRefs[i-1].current?.focus();
  }
  function handleCodePaste(e) {
    const p = e.clipboardData.getData('text').replace(/\D/g,'').slice(0,6);
    if (p.length===6) { setCode(p.split('')); codeRefs[5].current?.focus(); }
  }

  // ── Recovery token display (after register or reset) ──
  if (recoveryToken && registered) {
    return (
      <div className="auth-page">
      <div className="cloud-layer" aria-hidden="true"><div className="cloud c1"/><div className="cloud c2"/><div className="cloud c4"/><div className="cloud c6"/><div className="cloud c9"/></div>
        <div className="auth-card auth-card-wide">
          <div className="auth-logo">
            <span className="logo-icon">🔑</span>
            <h1>{t('recovery_token_title')}</h1>
            <p className="auth-tagline">{t('recovery_token_subtitle')}</p>
          </div>

          <div className="rt-box">
            <div className="rt-token">{recoveryToken}</div>
            <button className={`rt-copy ${tokenCopied?'copied':''}`} onClick={copyToken}>
              {tokenCopied ? t('recovery_token_copied') : t('recovery_token_copy')}
            </button>
          </div>

          <div className="rt-warnings">
            <div className="rt-warn-item">⚠️ <strong>{t('recovery_token_warn1_bold')}</strong> {t('recovery_token_warn1_rest')}</div>
            <div className="rt-warn-item">🔒 {t('recovery_token_warn2')}</div>
            <div className="rt-warn-item">🔐 {t('recovery_token_warn3')}</div>
          </div>

          <div style={{display:'flex', gap:10, flexWrap:'wrap'}}>
            <button className="btn-primary" style={{background:'#1e40af',flex:1}} onClick={() => {
              const content = [
                t('recovery_token_file_header'),
                '================================',
                '',
                t('recovery_token_file_account_label') + ' ' + registeredUsername,
                t('recovery_token_file_date_label') + ' ' + new Date().toLocaleString(document.documentElement.lang === 'es' ? 'es-ES' : document.documentElement.lang === 'fr' ? 'fr-FR' : document.documentElement.lang === 'de' ? 'de-DE' : 'en-US'),
                '',
                t('recovery_token_file_key_label'),
                recoveryToken,
                '',
                '================================',
                t('recovery_token_file_keep_safe'),
                t('recovery_token_file_lose_line1'),
                t('recovery_token_file_lose_line2'),
                '================================',
              ].join('\n');
              const blob = new Blob([content], { type: 'text/plain' });
              const url  = URL.createObjectURL(blob);
              const a    = document.createElement('a');
              a.href = url;
              a.download = `lifetimecloud-recovery-${registeredUsername}.txt`;
              document.body.appendChild(a); a.click();
              document.body.removeChild(a);
              setTimeout(() => URL.revokeObjectURL(url), 1000);
            }}>
              {t('recovery_token_download_btn')}
            </button>
            <button className="btn-primary" onClick={() => { setRecoveryToken(''); setRegistered(false); }}>
              {t('recovery_token_continue_btn')}
            </button>
          </div>
        </div>
      </div>
    );
  }

  // ── 2FA screen ────────────────────────────────────────
  if (pending2fa) {
    return (
      <div className="auth-page">
      <div className="cloud-layer" aria-hidden="true"><div className="cloud c1"/><div className="cloud c2"/><div className="cloud c4"/><div className="cloud c6"/><div className="cloud c9"/></div>
        <div className="auth-card">
          <div className="auth-logo">
            <span className="logo-icon">🔑</span>
            <h1>{t('twofa_title')}</h1>
            <p className="auth-tagline">{t('twofa_subtitle')}</p>
          </div>
          <form onSubmit={handle2fa} className="auth-form">
            <div className="code-inputs" onPaste={handleCodePaste}>
              {code.map((digit,i) => (
                <input key={i} ref={codeRefs[i]} type="text" inputMode="numeric" maxLength={1}
                  value={digit} onChange={e=>handleCodeInput(i,e.target.value)}
                  onKeyDown={e=>handleCodeKey(i,e)} className="code-box" autoFocus={i===0}/>
              ))}
            </div>
            {(pending2faError||error) && <div className="auth-error">⚠ {pending2faError||error}</div>}
            <button type="submit" className="btn-primary" disabled={loading}>{loading?t('twofa_verify_loading'):t('twofa_verify_btn')}</button>
            <button type="button" className="unlock-logout" onClick={cancel2fa}>{t('twofa_back_btn')}</button>
          </form>
        </div>
      </div>
    );
  }

  // ── Recovery flow — chiuso dal server (interruttore CAMBIO_PASSWORD_ATTIVO) ──
  if (showRecovery && !recuperoAttivo) {
    return (
      <div className="auth-page">
      <div className="cloud-layer" aria-hidden="true"><div className="cloud c1"/><div className="cloud c2"/><div className="cloud c4"/><div className="cloud c6"/><div className="cloud c9"/></div>
        <div className="auth-card">
          <div className="auth-logo">
            <span className="logo-icon">🔧</span>
            <h1>{t('recovery_title')}</h1>
          </div>
          <div className="auth-notice">
            <span>⏸️</span>
            <p>{t('recovery_disabled_notice')} <a href="mailto:info@lifetimecloud.me" style={{color:'var(--green)',fontWeight:700}}>info@lifetimecloud.me</a></p>
          </div>
          <button className="unlock-logout" onClick={()=>{setShowRecovery(false);setError('');}}>{t('recovery_back_to_login')}</button>
        </div>
      </div>
    );
  }

  // ── Recovery flow ─────────────────────────────────────
  if (showRecovery) {
    return (
      <div className="auth-page">
      <div className="cloud-layer" aria-hidden="true"><div className="cloud c1"/><div className="cloud c2"/><div className="cloud c4"/><div className="cloud c6"/><div className="cloud c9"/></div>
        <div className="auth-card">
          <div className="auth-logo">
            <span className="logo-icon">🔓</span>
            <h1>{t('recovery_title')}</h1>
            <p className="auth-tagline">
              {recoveryStep===1 ? t('recovery_tagline_step1') : t('recovery_tagline_step2')}
            </p>
          </div>

          {recoveryStep === 1 && (
            <form onSubmit={handleRecoveryVerify} className="auth-form">
              <div className="field">
                <label>{t('field_username_label')}</label>
                <input type="text" value={recoveryUsername} onChange={e=>setRecoveryUsername(e.target.value)}
                  placeholder={t('field_username_placeholder')} required autoComplete="username"/>
              </div>
              <div className="field">
                <label>{t('field_recovery_token_label')}</label>
                <input type="text" value={recoveryTokenIn} onChange={e=>setRecoveryTokenIn(e.target.value.toUpperCase())}
                  placeholder={t('field_recovery_token_placeholder')}
                  className="rt-input" required autoComplete="off"/>
                <small style={{color:'#8899bb',fontSize:11,marginTop:4,display:'block'}}>
                  {t('recovery_token_hint')}
                </small>
              </div>
              {error && <div className="auth-error">⚠ {error}</div>}
              <button type="submit" className="btn-primary" disabled={loading}>{loading?t('recovery_verify_loading'):t('recovery_continue_btn')}</button>
              <button type="button" className="unlock-logout" onClick={()=>{setShowRecovery(false);setError('');}}>{t('recovery_back_to_login')}</button>
            </form>
          )}

          {recoveryStep === 2 && (
            <form onSubmit={handleRecoveryReset} className="auth-form">
              <div className="auth-notice" style={{marginBottom:0}}>
                <span>✅</span>
                <p>{t('recovery_step2_notice')} <strong>@{recoveryUsername}</strong>.</p>
              </div>
              <div className="field">
                <label>{t('field_new_password_label')}</label>
                <input type="password" value={newPassword} onChange={e=>setNewPassword(e.target.value)}
                  placeholder={t('field_password_placeholder')} required autoComplete="new-password"/>
              </div>
              <div className="field">
                <label>{t('field_confirm_password_label')}</label>
                <input type="password" value={newConfirm} onChange={e=>setNewConfirm(e.target.value)}
                  placeholder={t('field_password_placeholder')} required autoComplete="new-password"/>
              </div>
              {error && <div className="auth-error">⚠ {error}</div>}
              <button type="submit" className="btn-primary" disabled={loading}>{loading?t('recovery_reset_loading'):t('recovery_reset_btn')}</button>
            </form>
          )}
        </div>
      </div>
    );
  }

  // ── Normal login / register ───────────────────────────
  return (
    <div className="auth-page">
      <div className="cloud-layer" aria-hidden="true"><div className="cloud c1"/><div className="cloud c2"/><div className="cloud c4"/><div className="cloud c6"/><div className="cloud c9"/></div>
      {onHome && <button type="button" className="auth-home-link" onClick={() => onHome()}>{t('auth_home_btn')}</button>}
      <div className="auth-card">
        <div className="auth-logo">
          <span className="logo-icon">🔐</span>
          <h1>LifetimeCloud</h1>
          <p className="auth-tagline">{t('auth_tagline')}</p>
        </div>

        {/* NIENTE SCHEDE, in nessun caso (2026-09-08).
            Restavano solo per chi apriva /register senza email precompilata:
            un clic diretto, o un link esterno. E' il visitatore FREDDO, quello
            che non ci conosce — e gli si metteva davanti una scelta fra due
            porte con "Accedi" per prima, cioe' un'uscita, nel momento in cui
            doveva solo scrivere due campi. Il 6 settembre 7 registrazioni su
            21 sono entrate da `da_login`, e una ha tentato un accesso fallito
            prima di capire di non avere un account.
            Ora quel caso vede un TITOLO che dice cosa ottiene, e chi ha gia'
            un account trova un link discreto sotto il modulo — la stessa
            forma, rovesciata, che il login usa gia' per la registrazione.
            L'accesso NON cambia, e chi arriva con l'email precompilata dalla
            hero continua a vedere "Ci sei quasi" come prima. */}
        {/* Spiega perche' si vede l'accesso invece della registrazione. Senza
            questa riga il visitatore che ha chiesto /register si troverebbe un
            modulo diverso da quello che ha chiesto, senza motivo apparente. */}
        {forzaLogin && (
          <div className="auth-notice">{t('already_registered_notice')}</div>
        )}
        {mode === 'login' ? null : (
          <h2 className="auth-step-title">
            {arrivedFromHero ? t('tab_register_confirm') : t('register_title_freddo')}
          </h2>
        )}

        <form onSubmit={mode==='login'?handleLogin:handleRegister} className="auth-form">
          {mode === 'login' && (
            <div className="field">
              <label>{t('field_username_label')}</label>
              {/* type="text", not "email" — this now also accepts a plain
                  username (every account registered before the email-based
                  register form) and type="email" would fail the browser's
                  own HTML5 validation on anything without an "@", blocking
                  submission before handleLogin ever runs. inputMode="email"
                  still gets the "@" key onto the mobile keyboard without
                  that constraint. */}
              <input type="text" inputMode="email" value={loginIdentifier} onChange={e=>setLoginIdentifier(e.target.value)}
                placeholder={t('field_username_placeholder')} required autoComplete="username" autoFocus/>
            </div>
          )}

          {mode === 'register' && (
            <div className="field">
              <label>{t('field_email_label')}</label>
              {showEmailField ? (
                <>
                  <input type="email" inputMode="email" value={email} onChange={e=>setEmail(e.target.value)}
                    placeholder={t('field_email_placeholder')} required autoComplete="email" autoFocus/>
                  {emailTypo.visible && (
                    <div className="email-suggestion">
                      <span>{t('email_suggestion_prefix')} <button type="button" className="email-suggestion-accept"
                        onClick={() => { setEmail(emailTypo.suggestion); emailTypo.markAccepted(); }}>{emailTypo.suggestion}</button>?</span>
                      <button type="button" className="email-suggestion-dismiss" onClick={emailTypo.dismiss} aria-label={t('email_suggestion_dismiss')}>✕</button>
                    </div>
                  )}
                </>
              ) : (
                <div className="auth-confirm-email">
                  <span className="auth-confirm-email-text">{t('confirm_email_prefix')} <strong>{email}</strong></span>
                  <button type="button" className="auth-confirm-email-change" onClick={() => setShowEmailField(true)}>
                    {t('confirm_email_change')}
                  </button>
                </div>
              )}
            </div>
          )}

          <div className="field">
            <label>{t('field_password_label')}</label>
            {mode === 'register' ? (
              <div className="field-password-wrap">
                <input type={showPassword ? 'text' : 'password'} value={password} onChange={e=>setPassword(e.target.value)}
                  placeholder={t('field_password_placeholder')} required autoComplete="new-password" autoFocus={!showEmailField}/>
                <button type="button" className="field-password-toggle"
                  onClick={()=>setShowPassword(s=>!s)}
                  aria-label={showPassword ? t('field_password_toggle_hide') : t('field_password_toggle_show')}>
                  {showPassword ? <EyeOffIcon /> : <EyeIcon />}
                </button>
              </div>
            ) : (
              <input type="password" value={password} onChange={e=>setPassword(e.target.value)}
                placeholder={t('field_password_placeholder')} required autoComplete="current-password"/>
            )}
            {mode === 'register' && (
              <small className={`password-hint ${password.length >= 8 ? 'valid' : ''}`}>
                {password.length >= 8 ? '✓ ' : ''}{t('password_requirements')}
              </small>
            )}
          </div>

          {error && <div className="auth-error">⚠ {error}</div>}

          <button type="submit" className="btn-primary" disabled={loading}>
            {loading ? t('submit_processing') : mode==='login' ? t('submit_login_btn') : t('submit_register_btn')}
          </button>

          {/* Una RIGA, non un modale (2026-09-04): anticipa che ci sara' un
              passaggio sulla chiave, senza metterlo qui. Lo schermo della
              chiave e' stato spostato al primo caricamento — vedi
              components/CerimoniaChiave.jsx — e questa riga esiste perche'
              l'utente non lo trovi a sorpresa. Un modale qui rifarebbe
              esattamente il danno che stiamo togliendo. */}
          {mode !== 'login' && (
            <p className="auth-nota-chiave">🔑 {t('nota_chiave_al_primo_upload')}</p>
          )}
        </form>

        {mode === 'login' && (
          <>
            <button className="unlock-logout" style={{marginTop:8}} onClick={()=>{setShowRecovery(true);setError('');setRecoveryStep(1);}}>
              {t('forgot_password_link')}
            </button>
            {/* Replaces the removed Register tab as the way back to register
                mode — a real navigable link (onSwitchToRegister) instead of
                a state setter, so the URL/back-button reflect reality. */}
            <p className="auth-switch-link">
              {t('no_account_prompt')}{' '}
              <a href="/register" className="auth-switch-link-btn" onClick={e=>{e.preventDefault();setError('');onSwitchToRegister();}}>
                {t('tab_register')}
              </a>
            </p>
          </>
        )}

        {/* Il collegamento per chi ha gia' un account: un link, non una
            scheda alla pari. Non si mostra a chi arriva dalla hero con
            l'email gia' scritta — quello ha appena scelto di registrarsi e
            un invito ad accedere sarebbe solo un modo per farlo uscire. */}
        {mode === 'register' && !arrivedFromHero && (
          <p className="auth-switch-link">
            {t('have_account_prompt')}{' '}
            <a href="/login" className="auth-switch-link-btn"
               onClick={e=>{e.preventDefault();setError('');onSwitchToLogin();}}>
              {t('tab_login')}
            </a>
          </p>
        )}

        {mode === 'register' && (
          <p className="auth-short-notice">🔒 {t('register_short_notice')}</p>
        )}

        {/* RIGA LEGALE RIMOSSA dal modulo di registrazione (2026-09-02).
            Non nascosta: tolta, insieme alle chiavi register_legal_prefix e
            register_legal_and dalle traduzioni, perche' non erano usate
            altrove. Erano due <button> in mezzo al modulo: chi ci cliccava
            distrattamente usciva dalla registrazione e perdeva quello che
            aveva scritto.
            L'informazione non sparisce: i due link restano nel pie' di pagina
            di tutte le pagine pubbliche, e l'email di verifica porta la riga
            "Registrandoti hai accettato..." con i due link — cosi' resta anche
            una traccia con data, che il modulo non dava. */}
      </div>
    </div>
  );
}
